import 'react-datepicker/dist/react-datepicker.css';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { Search, Eye, X } from 'lucide-react';
import downloadingRed from './../../assets/downloadingRed.png';
import moment from 'moment';
import { API } from '../../Common/Api';
import CommonTable from '../../Common/CommonTable';
import { ACTIONS } from '../../redux/actions';
import { ErrorToast } from '../../Utils/toast';
import Loading from '../../Common/Loading';
import TextInput from '../../Commponent/TextInput';
import CustomSelectInput from '../../Commponent/SelectInput';

const Report = () => {
  const dispatch = useDispatch();
  const GetReport = useSelector((state) => state.GetReport.GetReportData);

  const [checkReport, setCheckReport] = useState(false);
  const [agentData, setAgentData] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // "View tags" modal — lists the FASTags behind one report row. Every report
  // type is queryable this way (via Inventory.excleReport), so it's the one
  // place Manual Undo details are visible at all, since that flow has no file.
  const [viewReport, setViewReport] = useState(null); // { id, name } | null
  const [viewRows, setViewRows] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewPage, setViewPage] = useState(1);
  const [viewTotal, setViewTotal] = useState(0);
  const viewLimit = 50;

  const statusOptions = [
    { name: 'All Status', value: '' },
    { name: 'Completed', value: 'Completed' },
    { name: 'Processing', value: 'Processing' },
    { name: 'Pending', value: 'Pending' },
    { name: 'Failed', value: 'Failed' },
  ];

  const typeOptions = [
    { name: 'All Types', value: '' },
    { name: 'Onboarding', value: 'Onboarding' },
    { name: 'Reverse-File', value: 'Undo - File' },
    { name: 'Reverse', value: 'Undo - Manual' },
  ];

  const typeBadgeClass = {
    'Onboarding': 'bg-blue-50 text-blue-700 border-blue-100',
    'Undo - File': 'bg-amber-50 text-amber-700 border-amber-100',
    'Undo - Manual': 'bg-purple-50 text-purple-700 border-purple-100',
  };

  // Badge text — matches typeOptions' labels exactly (Reverse-File / Reverse),
  // not the raw stored value.
  const typeDisplayLabel = {
    'Onboarding': 'Onboarding',
    'Undo - File': 'Reverse-File',
    'Undo - Manual': 'Reverse',
  };

  // Debounce the name search.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 700);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCheckReport(true);
    dispatch(ACTIONS.GetReportAction({
      page: currentPage,
      limit: itemsPerPage,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(status && { status }),
      ...(type && { type }),
      ...(fromDate && { startDate: moment(fromDate).format('YYYY-MM-DD') }),
      ...(toDate && { endDate: moment(toDate).format('YYYY-MM-DD') }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, debouncedSearch, status, type, fromDate, toDate]);

  useEffect(() => {
    if (checkReport) {
      if (GetReport?.status === 200 && GetReport?.success) {
        setAgentData(GetReport?.data || []);
      } else {
        ErrorToast(GetReport?.message);
      }
      setCheckReport(false);
    }
  }, [GetReport]);

  const handleDownload = async (InventoryExcleURL) => {
    try {
      setLoading(true);
      let params = { InventoryExcleURL };
      const blob = await API.DownloadFile(JSON.stringify(params));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Inventory.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      ErrorToast("Failed to download file");
    } finally {
      setLoading(false);
    }
  };

  const openViewTags = (item) => {
    setViewReport({ id: item._id, name: item.name });
    setViewPage(1);
  };
  const closeViewTags = () => {
    setViewReport(null);
    setViewRows([]);
  };

  useEffect(() => {
    if (!viewReport) return;
    let cancelled = false;
    (async () => {
      setViewLoading(true);
      try {
        const res = await API.GetReportTags(viewReport.id, { page: viewPage, limit: viewLimit });
        if (cancelled) return;
        if (res?.status === 200 && res?.success) {
          setViewRows(Array.isArray(res.data) ? res.data : []);
          setViewTotal(res.pagination?.total || 0);
        } else {
          setViewRows([]); setViewTotal(0);
          ErrorToast(res?.message || 'Failed to load tag details');
        }
      } catch (e) {
        if (!cancelled) { setViewRows([]); setViewTotal(0); ErrorToast('Failed to load tag details'); }
      } finally {
        if (!cancelled) setViewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewReport, viewPage]);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setType('');
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  // CommonTable pads every cell px-5 py-4, which is roomy for a 10-column
  // report. These trim it for this table only.
  const CELL = '!px-2.5 !py-2';
  const HEAD = '!px-2.5 !py-2.5';

  const tableColumns = [
    {
      // Pinned so the row's identity stays visible while scrolling horizontally
      // through the rest of the table — it's grown to 9 columns and no longer
      // fits most viewports at once.
      label: "Name",
      key: "name",
      className: `sticky left-0 bg-[#FAFAFB] z-10 ${HEAD}`,
      tdClassName: `sticky left-0 bg-white z-10 !whitespace-normal ${CELL}`,
      render: (item) => (
        <span className="block max-w-[180px] break-words leading-snug font-medium text-gray-800">
          {item.name || '-'}
        </span>
      ),
    },
    {
      label: "Type",
      key: "reportType",
      className: HEAD,
      tdClassName: CELL,
      render: (item) => (
        // Rows created before this field existed have no reportType — shown
        // as "Unlabeled" rather than guessed, since they could be any of the
        // three kinds. Display swaps "Undo" for "Reverse" — the stored value
        // and typeBadgeClass lookup key are untouched, this is text only.
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${typeBadgeClass[item.reportType] || 'bg-gray-50 text-gray-500 border-gray-100'}`}>
          {typeDisplayLabel[item.reportType] || 'Unlabeled'}
        </span>
      )
    },
    {
      label: "Uploaded By",
      key: "user",
      className: HEAD,
      tdClassName: CELL,
      // A long single-word username would break mid-word if wrapped; clip it and
      // keep the full value in the tooltip instead.
      render: (item) => (
        <span className="block max-w-[120px] truncate" title={item.user?.username || "N/A"}>
          {item.user?.username || "N/A"}
        </span>
      )
    },
    {
      label: "Uploaded",
      key: "createdAt",
      className: HEAD,
      tdClassName: CELL,
      render: (item) => {
        if (!moment(item.createdAt).isValid()) return "-";
        const m = moment(item.createdAt);
        return (
          <div className="leading-tight">
            <div>{m.format("DD MMM YYYY")}</div>
            <div className="text-xs text-gray-400">{m.format("hh:mm A")}</div>
          </div>
        );
      }
    },
    {
      label: "Progress",
      key: "progressPercentage",
      className: `[&>div]:!justify-end ${HEAD}`,
      tdClassName: `text-right ${CELL}`,
      render: (item) => `${item.progressPercentage || 0}%`
    },
    {
      label: "Success",
      key: "successRecords",
      className: `[&>div]:!justify-end ${HEAD}`,
      tdClassName: `text-right ${CELL}`,
      render: (item) => (
        <span className={item.successRecords > 0 ? "text-green-600 font-semibold" : "text-gray-900 font-semibold"}>
          {item.successRecords || 0}
        </span>
      )
    },
    {
      label: "Failed",
      key: "failedRecords",
      className: `[&>div]:!justify-end ${HEAD}`,
      tdClassName: `text-right ${CELL}`,
      render: (item) => (
        <span className={item.failedRecords > 0 ? "text-red-600 font-semibold" : "text-gray-900 font-semibold"}>
          {item.failedRecords || 0}
        </span>
      )
    },
    {
      label: "Total",
      key: "totalRecords",
      className: `[&>div]:!justify-end ${HEAD}`,
      tdClassName: `text-right font-semibold ${CELL}`
    },
    {
      label: "Tags",
      className: `[&>div]:!justify-center ${HEAD}`,
      tdClassName: `text-center ${CELL}`,
      render: (item) => (
        <button
          onClick={() => openViewTags(item)}
          title="View the FASTags in this report"
          className="!p-1.5 inline-flex items-center justify-center bg-[#FFFFFF] border border-[#BEBEBE] !rounded-full transition duration-150 ease-in-out hover:border-[#EE3B31]"
        >
          <Eye className="w-[13px] h-[13px] text-gray-600" />
        </button>
      )
    },
    {
      label: "File",
      className: HEAD,
      tdClassName: `text-center ${CELL}`,
      render: (item) => (
        // A Manual Undo never uploads a file, so it has no InventoryExcleURL —
        // there is genuinely nothing to download. Disable rather than let the
        // click hit the API and fail with a generic error toast.
        item.InventoryExcleURL ? (
          <button
            onClick={() => handleDownload(item.InventoryExcleURL)}
            className="!p-1.5 inline-flex items-center justify-center bg-[#FFFFFF] border border-[#BEBEBE] !rounded-full transition duration-150 ease-in-out"
          >
            <img alt="download" src={downloadingRed} className="w-[13px] h-[13px]" />
          </button>
        ) : (
          <span className="text-xs text-gray-300" title="No file for this report">—</span>
        )
      )
    }
  ];

  // Server-side pagination from API response
  const total = GetReport?.pagination?.total || 0;
  const totalPages = GetReport?.pagination?.totalPages || 1;
  const paginatedData = agentData;

  return (
    <>
      <Loading loading={loading} />
      <div className="flex items-center gap-3 mb-4 mt-6">
        <span className="w-1.5 h-7 rounded-full bg-[#EE3B31]"></span>
        <h2 className="text-2xl font-bold text-gray-900 leading-tight">Reports</h2>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-center">
          {/* Name search */}
          <div className="relative">
            <TextInput
              name="search"
              placeholder="Search by Name"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              inputClass="pl-5 pr-10 py-3 text-sm"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search className="w-4 h-4" />
            </div>
          </div>

          {/* Report type — Onboarding stock upload vs the two undo flows */}
          <CustomSelectInput
            name="type"
            value={type}
            onChange={(e) => { setType(e.target.value); setCurrentPage(1); }}
            options={typeOptions}
            inputClass="py-3 px-3 text-sm"
          />

          {/* Status (success) filter */}
          <CustomSelectInput
            name="status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
            options={statusOptions}
            inputClass="py-3 px-3 text-sm"
          />

          {/* From date */}
          <div className="relative w-full">
            <DatePicker
              selected={fromDate}
              onChange={(d) => { setFromDate(d); setCurrentPage(1); }}
              dateFormat="dd MMM, yyyy"
              placeholderText="From Date"
              isClearable
              maxDate={toDate || new Date()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20"
            />
          </div>

          {/* To date */}
          <div className="relative w-full">
            <DatePicker
              selected={toDate}
              onChange={(d) => { setToDate(d); setCurrentPage(1); }}
              dateFormat="dd MMM, yyyy"
              placeholderText="To Date"
              isClearable
              minDate={fromDate || undefined}
              maxDate={new Date()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20"
            />
          </div>

          {/* Clear */}
          <div className="flex justify-start lg:justify-end">
            <button
              onClick={clearFilters}
              className="border border-[#EE3B31] text-[#EE3B31] py-2.5 px-5 rounded-lg font-semibold transition-colors hover:bg-[#EE3B31] hover:text-white text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="p-0 space-y-8">
        {/* Table with Pagination */}
        <CommonTable
          columns={tableColumns}
          data={paginatedData}
          emptyMessage="No Reports Found"
          isPagination={true}
          paginationClass="!mt-4"
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          setItemsPerPage={setItemsPerPage}
          total={total}
          totalPages={totalPages}
          loading={checkReport}
          hideDateAndDownload={true}
        />
      </div>

      {/* View tags modal */}
      {viewReport && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-100 shrink-0">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate">Tags in this report</h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{viewReport.name}</p>
              </div>
              <button onClick={closeViewTags} className="p-2 text-gray-400 hover:text-[#EE3B31] hover:bg-red-50 rounded-full transition-all">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              <table className="min-w-full text-sm text-left">
                <thead className="sticky top-0 bg-[#FAFAFB] border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Tag Ref ID</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Vehicle Class</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Current Holder</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {viewLoading ? (
                    <tr><td colSpan={4} className="text-center text-gray-400 py-10">Loading…</td></tr>
                  ) : viewRows.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-gray-400 py-10">No tags found for this report</td></tr>
                  ) : viewRows.map((r) => (
                    <tr key={r._id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3 font-medium text-gray-800 break-all">{r.tagRefId}</td>
                      <td className="px-4 py-3 text-gray-600">{r.vehicleClass || 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className="block leading-tight">{r.currentHolder}</span>
                        <span className="block text-[11px] text-gray-400 capitalize">{r.holderRole}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-100">
                          {r.isActivated ? 'Activated' : (r.status || 'N/A')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {viewTotal > viewLimit && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 shrink-0 text-sm text-gray-500">
                <span>{viewTotal} tag(s) total</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewPage((p) => Math.max(1, p - 1))}
                    disabled={viewPage === 1 || viewLoading}
                    className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <span>{viewPage} / {Math.max(1, Math.ceil(viewTotal / viewLimit))}</span>
                  <button
                    onClick={() => setViewPage((p) => Math.min(Math.ceil(viewTotal / viewLimit), p + 1))}
                    disabled={viewPage >= Math.ceil(viewTotal / viewLimit) || viewLoading}
                    className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Report;
